/** Sprint 5-b PWA · 紀錄 tab · 會議紀錄歷史（過往會議·點入看 minutes）。
 *  backend #268 無「列出所有 minutes」端點 → 以過往 meeting 事件為列表，點入讀該場 minutes。
 *  （真正的 minutes list 端點列為 backend follow-up。） */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Stack, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';

import { useMessage } from '../../../contexts/MessageContext';
import { formatLocal } from '../../../utils/dateHelper';
import { getEvents } from '../../../services/eeMyCalendarService';
import { EeMyCalendar } from '../../../types/eeMyCalendar';

const MinutesHistoryPage: React.FC = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pwaMinutesHist.${k}`, d);
  const { showApiError } = useMessage();
  const [items, setItems] = useState<EeMyCalendar[]>([]);

  const load = useCallback(async () => {
    const now = new Date();
    const start = new Date(now.getTime() - 90 * 864e5);   // 近 90 天
    try {
      const list = await getEvents({ start: start.toISOString(), end: now.toISOString() });
      setItems(list
        .filter((e) => e.event_type === 'meeting')
        .sort((a, b) => (b.dtstart || '').localeCompare(a.dtstart || '')));   // 新到舊
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ my: 1 }}>📋 {tt('title', '會議紀錄')}</Typography>
      <Typography variant="caption" color="text.secondary">{tt('hint', '近 90 天 · 點入查看紀錄（未產生則無內容）')}</Typography>

      <Stack spacing={1} sx={{ mt: 1 }}>
        {items.length === 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">{tt('empty', '近期沒有會議紀錄。')}</Typography>
          </Paper>
        )}
        {items.map((e) => (
          <Paper key={e.id} variant="outlined" sx={{ p: 1.5 }}
            onClick={() => nav(`/ee_pwa_meetings/${e.id}/minutes`)}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DescriptionIcon color="action" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" noWrap>{e.summary}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {e.dtstart ? formatLocal(e.dtstart) : ''}</Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default MinutesHistoryPage;
